/**
 * PD-16593 — vocabulary the Recent Changes list shares between its filter tray
 * (libs/core), the list endpoint (crm-api) and the page.
 *
 * Option ids are deliberately not numeric: the list's URL state turns any id
 * `parseInt` can read into a number (ListStateStorageService.parseUrlParams),
 * which would break a shared link — so the windows are named, not counted.
 */

/** How far back the list reads. The default is the last 7 days (Chris 2026-09-27). */
export const RECENT_MATCH_CHANGE_WINDOWS = [
	{ id: 'day', label: 'Last 24 hours', days: 1 },
	{ id: 'week', label: 'Last 7 days', days: 7 },
	{ id: 'month', label: 'Last 30 days', days: 30 },
	{ id: 'quarter', label: 'Last 90 days', days: 90 },
] as const satisfies ReadonlyArray<{ id: string; label: string; days: number }>;

export type RecentMatchChangeWindowId =
	(typeof RECENT_MATCH_CHANGE_WINDOWS)[number]['id'];

export const RECENT_MATCH_CHANGE_DEFAULT_WINDOW: RecentMatchChangeWindowId =
	'week';

/**
 * Whose decisions the list shows. The default is the caller's own; teammates
 * and Workd Automation (the automation user, and linked licenses confirmed
 * automatically) are opt-in.
 */
export const RECENT_MATCH_CHANGE_WHO = [
	{ id: 'me', label: 'My changes' },
	{ id: 'team', label: 'Teammates' },
	{ id: 'automation', label: 'Workd Automation' },
] as const satisfies ReadonlyArray<{ id: string; label: string }>;

export type RecentMatchChangeWhoId =
	(typeof RECENT_MATCH_CHANGE_WHO)[number]['id'];

/**
 * PD-16602 — the rejection reason an undone Workd Automation confirm leaves on
 * the match (`rejection_reason`) and in its reject's log details. An internal
 * code: never shown to a person (the Recent Changes row says it in words and
 * flags the row `undoneAutomationConfirm`).
 */
export const AUTOMATION_CONFIRM_UNDONE_REASON = 'automation_confirm_undone';

/** The decisions the list shows, in the words the list uses. */
export const RECENT_MATCH_CHANGE_ACTIONS = [
	{ id: 'confirmed', label: 'Confirmed' },
	{ id: 'rejected', label: 'Rejected' },
	{ id: 'skipped', label: 'Skipped' },
	{ id: 'manual_matched', label: 'Matched by hand' },
	{ id: 'unmatched', label: 'Unmatched' },
	{ id: 'undone', label: 'Undone' },
	{ id: 'reverted', label: 'Moved back to review' },
] as const satisfies ReadonlyArray<{ id: string; label: string }>;

/**
 * The number of days a window id stands for; undefined for anything else. A
 * bare number of days that matches a window is accepted too (API callers).
 */
export function recentMatchChangeWindowDays(id: unknown): number | undefined {
	const window = RECENT_MATCH_CHANGE_WINDOWS.find(
		(w) => w.id === id || (typeof id === 'number' && w.days === id),
	);
	return window?.days;
}
